import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "budget-revision-page" }
const _hoisted_2 = {
  key: 0,
  class: "budget-revision-empty"
}
const _hoisted_3 = { class: "budget-revision-toolbar" }
const _hoisted_4 = { class: "bank-tabs-wrapper budget-revision-tabs-wrapper" }
const _hoisted_5 = { class: "bank-tabs" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "bank-alias" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = { class: "budget-revision-add-card__choices" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = { class: "rev-choice__text" }
const _hoisted_12 = { key: 0 }
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { class: "budget-revision-add-card__kicker" }
const _hoisted_15 = { class: "budget-revision-add-card__lead" }
const _hoisted_16 = { class: "budget-revision-add-card__choices" }
const _hoisted_17 = { class: "rev-choice__text" }
const _hoisted_18 = {
  key: 0,
  class: "budget-revision-add-card__delete-note"
}
const _hoisted_19 = {
  key: 1,
  class: "budget-revision-add-card__delete-note"
}
const _hoisted_20 = { class: "budget-revision-snack-body" }
const _hoisted_21 = { class: "budget-revision-snack-msg" }
const _hoisted_22 = {
  key: 0,
  class: "budget-revision-snack-warnings"
}

import { ref, computed, inject, watch, onMounted, type Ref } from "vue";
import { usePageTabNav } from "@/composables/usePageTabNav";
import { useRoute } from "vue-router";
import BudgetMainPanel from "@/components/budget/BudgetMainPanel.vue";
import { budgetApi } from "@/services/api";


export default /*@__PURE__*/_defineComponent({
  __name: 'MemberBudgetRevision',
  setup(__props) {

const route = useRoute();

const memberDataRef = inject<Ref<{ m_code?: string } | null>>("memberData");
const selectedBizGubunRef = inject<Ref<string>>("selectedBizGubun");

const memberCode = computed(() => {
  const fromRoute = String(route.params.memberCode || "").trim();
  if (fromRoute) return fromRoute;
  const m = memberDataRef?.value?.m_code;
  return m != null && String(m).trim() ? String(m) : "";
});

const bizGubun = computed(() => selectedBizGubunRef?.value || "");

const canUseBudget = computed(() => Boolean(memberCode.value) && Boolean(bizGubun.value));

const revisionYear = ref(String(new Date().getFullYear()));
const revisionTabs = ref<{ id: string; label: string }[]>([{ id: "", label: "본예산" }]);
const selectedRevisionTabId = ref<string>("");
/** DB에 행이 있는 차수(빈 로컬 탭과 구분 — 삭제·직전복사에 사용) */
const savedBudClassIds = ref<Set<string>>(new Set([""]));

const selectedBudgetClass = computed(() => selectedRevisionTabId.value);

const budgetPanelRef = ref<{ confirmDiscard: () => Promise<boolean> } | null>(null);
const showRevisionManageDialog = ref(false);
const revisionManageMode = ref<"create" | "delete">("create");
const addRevisionMode = ref<"keep" | "empty">("keep");
const addRevisionLoading = ref(false);
const deleteRevisionLoading = ref(false);

const revisionSnack = ref({
  show: false,
  message: "",
  warnings: [] as string[],
});

function showRevisionSnack(message: string, warnings: string[] = []) {
  revisionSnack.value = {
    show: true,
    message,
    warnings: warnings.filter(Boolean),
  };
}

function onPanelYearChange(y: string) {
  revisionYear.value = y;
}

function tabLabel(budClass: string): string {
  const c = budClass != null ? String(budClass) : "";
  if (!c || c === "0") return "본예산";
  return `${c}차 추경`;
}

function normalizeBudId(c: string | null | undefined): string {
  const s = c != null ? String(c) : "";
  return s === "0" ? "" : s;
}

/** 저장된(DB) 추경 중 가장 큰 차수 — 빈 로컬 탭 제외 */
const maxSavedNumericRevisionId = computed(() => {
  const nums = [...savedBudClassIds.value]
    .filter((id) => id !== "")
    .map((id) => parseInt(id, 10))
    .filter((n) => !Number.isNaN(n));
  return nums.length ? Math.max(...nums) : 0;
});

const hasNumericRevision = computed(() => maxSavedNumericRevisionId.value > 0);

/** 다음 차수는 저장된 마지막 번호의 다음. 저장 안 된 빈 탭은 번호를 건너뛰지 않는다. */
const nextBudClass = computed(() => String(maxSavedNumericRevisionId.value + 1));

const lastRevisionLabel = computed(() => {
  if (!hasNumericRevision.value) return "본예산";
  return tabLabel(String(maxSavedNumericRevisionId.value));
});

const previousSheetLabel = computed(() => lastRevisionLabel.value);

/** 저장본의 마지막 차수, 또는 아직 저장하지 않은 다음 차수 빈 탭만 지울 수 있다. */
const canDeleteCurrent = computed(() => {
  const bud = selectedRevisionTabId.value;
  if (!bud) return false;
  if (!savedBudClassIds.value.has(bud)) return bud === nextBudClass.value;
  return bud === String(maxSavedNumericRevisionId.value);
});

async function loadBudTabs() {
  if (!canUseBudget.value) {
    revisionTabs.value = [{ id: "", label: "본예산" }];
    selectedRevisionTabId.value = "";
    savedBudClassIds.value = new Set([""]);
    return;
  }
  try {
    const res = await budgetApi.getBudClasses({
      memberCode: memberCode.value,
      year: revisionYear.value,
      bizGubun: bizGubun.value,
    });
    const raw = (res.data as { data?: string[] }).data;
    const fromApi = Array.isArray(raw) ? raw.map((x) => normalizeBudId(x)) : [""];
    const uniq = [...new Set(fromApi.length ? fromApi : [""])].sort((a, b) => {
      if (a === "") return -1;
      if (b === "") return 1;
      const na = parseInt(a, 10);
      const nb = parseInt(b, 10);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return a.localeCompare(b, "ko");
    });
    const fromApiTabs = uniq.map((id) => ({ id, label: tabLabel(id) }));
    const apiIds = new Set(fromApiTabs.map((t) => t.id));
    savedBudClassIds.value = apiIds;
    const savedNums = [...apiIds]
      .filter((id) => id !== "")
      .map((id) => parseInt(id, 10))
      .filter((n) => !Number.isNaN(n));
    const pendingId = String((savedNums.length ? Math.max(...savedNums) : 0) + 1);
    const localOnly = revisionTabs.value.filter((t) => t.id === pendingId && !apiIds.has(t.id));
    revisionTabs.value = [...fromApiTabs, ...localOnly].sort((a, b) => {
      if (a.id === "") return -1;
      if (b.id === "") return 1;
      const na = parseInt(a.id, 10);
      const nb = parseInt(b.id, 10);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return a.id.localeCompare(b.id, "ko");
    });
    const cur = selectedRevisionTabId.value;
    if (!revisionTabs.value.some((t) => t.id === cur)) {
      selectedRevisionTabId.value = "";
    }
  } catch {
    revisionTabs.value = [{ id: "", label: "본예산" }];
    selectedRevisionTabId.value = "";
    savedBudClassIds.value = new Set([""]);
  }
}

async function selectRevisionTab(id: string) {
  if (id === selectedRevisionTabId.value) return;
  if (budgetPanelRef.value && !(await budgetPanelRef.value.confirmDiscard())) return;
  selectedRevisionTabId.value = id;
}

const budgetPageTabs = computed(() =>
  revisionTabs.value.map((tab) => ({ key: tab.id, title: tab.label }))
);
usePageTabNav("budget-revision", budgetPageTabs, selectedRevisionTabId, selectRevisionTab);

async function addEmptyRevisionTabOnly() {
  const next = nextBudClass.value;
  if (budgetPanelRef.value && !(await budgetPanelRef.value.confirmDiscard())) return;
  if (revisionTabs.value.some((t) => t.id === next)) {
    selectedRevisionTabId.value = next;
    showRevisionSnack(`${next}차 추경 빈 시트가 이미 열려 있습니다. 입력 후 저장하세요.`);
    return;
  }
  revisionTabs.value.push({ id: next, label: `${next}차 추경` });
  revisionTabs.value.sort((a, b) => {
    if (a.id === "") return -1;
    if (b.id === "") return 1;
    return parseInt(a.id, 10) - parseInt(b.id, 10);
  });
  selectedRevisionTabId.value = next;
  showRevisionSnack(`${next}차 추경 빈 시트가 추가되었습니다. 입력 후 저장하세요.`);
}

function openRevisionManageDialog() {
  if (!canUseBudget.value) return;
  revisionManageMode.value = "create";
  addRevisionMode.value = "keep";
  showRevisionManageDialog.value = true;
}

async function confirmDeleteCurrentRevision() {
  const bud = selectedRevisionTabId.value;
  if (!bud || !canDeleteCurrent.value) return;
  deleteRevisionLoading.value = true;
  try {
    // 저장 안 된 빈 시트: 서버 차수 가드(나중 차수 존재)에 막히지 않도록 로컬만 닫음
    if (!savedBudClassIds.value.has(bud)) {
      showRevisionManageDialog.value = false;
      selectedRevisionTabId.value = "";
      revisionTabs.value = revisionTabs.value.filter((t) => t.id !== bud);
      showRevisionSnack(`${tabLabel(bud)} 빈 시트를 닫았습니다. (아직 저장된 행이 없습니다)`);
      return;
    }

    const res = await budgetApi.deleteYear({
      memberCode: memberCode.value,
      year: revisionYear.value,
      budClass: bud,
      bizGubun: bizGubun.value,
    });
    const data = res.data as { success?: boolean; message?: string };
    if (!data?.success) {
      showRevisionSnack(`추경 삭제 실패: ${data?.message || "알 수 없는 오류"}`);
      return;
    }
    showRevisionManageDialog.value = false;
    selectedRevisionTabId.value = "";
    await loadBudTabs();
    revisionTabs.value = revisionTabs.value.filter((t) => t.id !== bud);
    showRevisionSnack(`${tabLabel(bud)} 시트와 추경관리 입력을 삭제했습니다.`);
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string }; status?: number } };
    const msg = err.response?.data?.message || (error instanceof Error ? error.message : "알 수 없는 오류");
    const st = err.response?.status;
    showRevisionSnack(`추경 삭제 실패: ${msg}${st ? ` (${st})` : ""}`);
  } finally {
    deleteRevisionLoading.value = false;
  }
}

function onBudgetSaved() {
  void loadBudTabs();
}

async function confirmAddRevision() {
  if (budgetPanelRef.value && !(await budgetPanelRef.value.confirmDiscard())) return;
  const to = nextBudClass.value;
  const fromLabel = previousSheetLabel.value;

  if (addRevisionMode.value === "empty") {
    addEmptyRevisionTabOnly();
    showRevisionManageDialog.value = false;
    return;
  }

  // 직전 시트만. 본예산이면 빈 차수, 추경이면 저장된 마지막 번호. 빈 로컬 탭은 원본이 아니다.
  const from = maxSavedNumericRevisionId.value > 0 ? String(maxSavedNumericRevisionId.value) : "";

  addRevisionLoading.value = true;
  try {
    const res = await budgetApi.cloneBudClass({
      memberCode: memberCode.value,
      year: revisionYear.value,
      budClassFrom: from,
      budClassTo: to,
      bizGubun: bizGubun.value,
    });
    const data = res.data;
    if (!data?.success) {
      const msg = data?.message || "알 수 없는 오류";
      showRevisionSnack(`추경 복사 실패: ${msg}`);
      return;
    }
    const copied = data.copied ?? 0;
    const warnings = Array.isArray(data.warnings) ? data.warnings : [];
    showRevisionManageDialog.value = false;
    await loadBudTabs();
    selectedRevisionTabId.value = to;
    if (warnings.length) {
      showRevisionSnack(
        `${to}차 추경에 ${fromLabel} 항목 ${copied}건을 그대로 넣었습니다. 아래 짝·그룹 점검 메시지를 확인하세요.`,
        warnings
      );
    } else {
      showRevisionSnack(
        `${to}차 추경에 ${fromLabel} 항목 ${copied}건을 그대로 넣었습니다.`
      );
    }
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string }; status?: number } };
    const msg = err.response?.data?.message || (error instanceof Error ? error.message : "알 수 없는 오류");
    const st = err.response?.status;
    showRevisionSnack(`추경 복사 실패: ${msg}${st ? ` (${st})` : ""}`);
  } finally {
    addRevisionLoading.value = false;
  }
}

watch([memberCode, bizGubun, revisionYear], () => {
  void loadBudTabs();
});

onMounted(() => {
  void loadBudTabs();
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!canUseBudget.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_component_v_icon, {
            size: "48",
            color: "#90a4ae"
          }, {
            default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
              _createTextVNode("mdi-alert-circle-outline", -1)
            ]))]),
            _: 1
          }),
          _cache[10] || (_cache[10] = _createElementVNode("p", null, "사업구분을 선택한 뒤 예산을 작성할 수 있습니다.", -1))
        ]))
      : (_openBlock(), _createBlock(BudgetMainPanel, {
          key: 1,
          ref_key: "budgetPanelRef",
          ref: budgetPanelRef,
          "use-member-page-layout": "",
          "hide-remote-budget-delete": "",
          "member-code": memberCode.value,
          "biz-gubun": bizGubun.value,
          year: revisionYear.value,
          "budget-class": selectedBudgetClass.value,
          onYearChange: onPanelYearChange,
          onBudgetSaved: onBudgetSaved,
          onBudgetDeleted: onBudgetSaved
        }, {
          toolbar: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(revisionTabs.value, (tab) => {
                    return (_openBlock(), _createElementBlock("button", {
                      key: tab.id === '' ? 'main' : tab.id,
                      type: "button",
                      class: _normalizeClass(["bank-tab", { active: selectedRevisionTabId.value === tab.id }]),
                      onClick: ($event: any) => (selectRevisionTab(tab.id))
                    }, [
                      _createVNode(_component_v_icon, {
                        size: "14",
                        class: "bank-icon"
                      }, {
                        default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                          _createTextVNode("mdi-file-document-edit-outline", -1)
                        ]))]),
                        _: 1
                      }),
                      _createElementVNode("span", _hoisted_7, _toDisplayString(tab.label), 1)
                    ], 10, _hoisted_6))
                  }), 128)),
                  _createElementVNode("button", {
                    type: "button",
                    class: "bank-tab budget-revision-manage-tab",
                    onClick: openRevisionManageDialog
                  }, [
                    _createVNode(_component_v_icon, { size: "16" }, {
                      default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                        _createTextVNode("mdi-tune-variant", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[13] || (_cache[13] = _createElementVNode("span", null, "추경관리", -1))
                  ])
                ])
              ])
            ])
          ]),
          _: 1
        }, 8, ["member-code", "biz-gubun", "year", "budget-class"])),
    _createVNode(_component_v_dialog, {
      modelValue: showRevisionManageDialog.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showRevisionManageDialog).value = $event)),
      class: "budget-page-dialog",
      "max-width": "520",
      scrollable: ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, { class: "budget-revision-add-card" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "budget-revision-add-card__title" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, {
                  size: "22",
                  color: "#1e3a5f"
                }, {
                  default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                    _createTextVNode("mdi-file-document-edit-outline", -1)
                  ]))]),
                  _: 1
                }),
                _cache[16] || (_cache[16] = _createElementVNode("span", null, "추경 관리", -1)),
                _createElementVNode("button", {
                  type: "button",
                  class: "dialog-close-x",
                  title: "닫기",
                  disabled: addRevisionLoading.value || deleteRevisionLoading.value,
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (showRevisionManageDialog.value = false))
                }, [
                  _createVNode(_component_v_icon, { size: "18" }, {
                    default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                      _createTextVNode("mdi-close", -1)
                    ]))]),
                    _: 1
                  })
                ], 8, _hoisted_8)
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_text, { class: "budget-revision-add-card__body" }, {
              default: _withCtx(() => [
                (selectedRevisionTabId.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _cache[20] || (_cache[20] = _createElementVNode("p", { class: "budget-revision-add-card__kicker" }, "할 일", -1)),
                      _createElementVNode("div", _hoisted_9, [
                        _createElementVNode("button", {
                          type: "button",
                          class: _normalizeClass(["rev-choice", { 'is-on': revisionManageMode.value === 'create' }]),
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (revisionManageMode.value = 'create'))
                        }, [...(_cache[17] || (_cache[17] = [
                          _createElementVNode("span", { class: "rev-choice__mark" }, null, -1),
                          _createElementVNode("span", { class: "rev-choice__text" }, [
                            _createElementVNode("strong", null, "추경 새로 만들기"),
                            _createElementVNode("em", null, "다음 차수 시트를 만듭니다.")
                          ], -1)
                        ]))], 2),
                        _createElementVNode("button", {
                          type: "button",
                          class: _normalizeClass(["rev-choice", { 'is-on': revisionManageMode.value === 'delete' }]),
                          disabled: !canDeleteCurrent.value,
                          onClick: _cache[2] || (_cache[2] = ($event: any) => (revisionManageMode.value = 'delete'))
                        }, [
                          _cache[19] || (_cache[19] = _createElementVNode("span", { class: "rev-choice__mark" }, null, -1)),
                          _createElementVNode("span", _hoisted_11, [
                            _cache[18] || (_cache[18] = _createElementVNode("strong", null, "현재 추경 삭제", -1)),
                            (canDeleteCurrent.value)
                              ? (_openBlock(), _createElementBlock("em", _hoisted_12, "지금 보고 있는 추경 시트를 지웁니다."))
                              : (_openBlock(), _createElementBlock("em", _hoisted_13, "마지막 차수(" + _toDisplayString(lastRevisionLabel.value) + ")만 지울 수 있습니다.", 1))
                          ])
                        ], 10, _hoisted_10)
                      ])
                    ], 64))
                  : _createCommentVNode("", true),
                (revisionManageMode.value === 'create')
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createElementVNode("p", _hoisted_14, _toDisplayString(nextBudClass.value) + "차 추경", 1),
                      _createElementVNode("p", _hoisted_15, _toDisplayString(previousSheetLabel.value) + "을 그대로 둘지, 빈 시트로 시작할지 고릅니다. 그대로 두면 적혀 있던 항목만 옮깁니다. 보험·식재료비·수가를 새로 만들어 넣지 않습니다.", 1),
                      _createElementVNode("div", _hoisted_16, [
                        _createElementVNode("button", {
                          type: "button",
                          class: _normalizeClass(["rev-choice", { 'is-on': addRevisionMode.value === 'keep' }]),
                          onClick: _cache[3] || (_cache[3] = ($event: any) => (addRevisionMode.value = 'keep'))
                        }, [
                          _cache[22] || (_cache[22] = _createElementVNode("span", { class: "rev-choice__mark" }, null, -1)),
                          _createElementVNode("span", _hoisted_17, [
                            _cache[21] || (_cache[21] = _createElementVNode("strong", null, "직전 그대로 두기", -1)),
                            _createElementVNode("em", null, _toDisplayString(previousSheetLabel.value) + "의 금액을 " + _toDisplayString(nextBudClass.value) + "차에 그대로 넣습니다.", 1)
                          ])
                        ], 2),
                        _createElementVNode("button", {
                          type: "button",
                          class: _normalizeClass(["rev-choice", { 'is-on': addRevisionMode.value === 'empty' }]),
                          onClick: _cache[4] || (_cache[4] = ($event: any) => (addRevisionMode.value = 'empty'))
                        }, [...(_cache[23] || (_cache[23] = [
                          _createElementVNode("span", { class: "rev-choice__mark" }, null, -1),
                          _createElementVNode("span", { class: "rev-choice__text" }, [
                            _createElementVNode("strong", null, "빈 시트로 시작"),
                            _createElementVNode("em", null, "금액 없이 칸만 만듭니다.")
                          ], -1)
                        ]))], 2)
                      ])
                    ], 64))
                  : (selectedRevisionTabId.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                        (canDeleteCurrent.value)
                          ? (_openBlock(), _createElementBlock("p", _hoisted_18, [
                              _createElementVNode("strong", null, _toDisplayString(tabLabel(selectedRevisionTabId.value)), 1),
                              _cache[24] || (_cache[24] = _createTextVNode(" 예산 시트와 같은 차수의 입력 내역을 함께 지웁니다. 지운 내용은 되돌릴 수 없습니다. 다시 만들면 같은 차수 번호가 비어 그 번호로 만듭니다. ", -1))
                            ]))
                          : (_openBlock(), _createElementBlock("p", _hoisted_19, " 마지막 차수(" + _toDisplayString(lastRevisionLabel.value) + ")만 지울 수 있습니다. 그 시트를 연 뒤 삭제하세요. ", 1))
                      ], 64))
                    : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, { class: "budget-revision-add-card__actions" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  variant: "outlined",
                  disabled: addRevisionLoading.value || deleteRevisionLoading.value,
                  onClick: _cache[5] || (_cache[5] = ($event: any) => (showRevisionManageDialog.value = false))
                }, {
                  default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                    _createTextVNode("취소", -1)
                  ]))]),
                  _: 1
                }, 8, ["disabled"]),
                (revisionManageMode.value === 'create')
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 0,
                      color: "primary",
                      variant: "elevated",
                      loading: addRevisionLoading.value,
                      onClick: confirmAddRevision
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, { size: "18" }, {
                          default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                            _createTextVNode("mdi-check", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[27] || (_cache[27] = _createTextVNode(" 만들기 ", -1))
                      ]),
                      _: 1
                    }, 8, ["loading"]))
                  : (_openBlock(), _createBlock(_component_v_btn, {
                      key: 1,
                      color: "error",
                      variant: "elevated",
                      loading: deleteRevisionLoading.value,
                      disabled: !canDeleteCurrent.value,
                      onClick: confirmDeleteCurrentRevision
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, { size: "18" }, {
                          default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                            _createTextVNode("mdi-delete", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[29] || (_cache[29] = _createTextVNode(" 삭제 ", -1))
                      ]),
                      _: 1
                    }, 8, ["loading", "disabled"]))
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_snackbar, {
      modelValue: revisionSnack.value.show,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((revisionSnack.value.show) = $event)),
      timeout: revisionSnack.value.warnings.length ? 16000 : 5000,
      color: "#1e3a5f",
      location: "top",
      class: "budget-revision-snackbar"
    }, {
      actions: _withCtx(() => [
        _createVNode(_component_v_btn, {
          variant: "text",
          class: "text-white",
          onClick: _cache[7] || (_cache[7] = ($event: any) => (revisionSnack.value.show = false))
        }, {
          default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
            _createTextVNode("닫기", -1)
          ]))]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_20, [
          _createElementVNode("p", _hoisted_21, _toDisplayString(revisionSnack.value.message), 1),
          (revisionSnack.value.warnings.length)
            ? (_openBlock(), _createElementBlock("ul", _hoisted_22, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(revisionSnack.value.warnings, (w, i) => {
                  return (_openBlock(), _createElementBlock("li", { key: i }, _toDisplayString(w), 1))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "timeout"])
  ]))
}
}

})